import {
  Component, OnInit, OnDestroy, OnChanges, Input, Output, EventEmitter,
  SimpleChanges, ViewChild, ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, debounceTime, filter, switchMap, tap } from 'rxjs/operators';

import SwalComponent from './../../../utils/custom-swal.component';
import uuid from '../../../utils/uuid';
import { MessageService } from './../../../message.service';
import { UserProfileService } from './../../../api/user-profile.service';
import { FeeSplitsService } from './../../../api/fee-splits.service';
import { FormService } from './../../../api/form.service';

import { FormServiceObject, PsProjectObject, FieldOptionsObject } from './../../../interfaces/form-service-object';
import { DataObject } from '../../../interfaces/data-objects';

@UntilDestroy()
@Component({
  selector: 'iv-fee-split',
  templateUrl: './fee-split.component.html',
  styleUrls: ['./fee-split.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeeSplitComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('warnAlert', { static: true }) private warnAlert: SwalComponent;
  @Input() showDisbursement: boolean = true;
  @Input() feeSplit: any;
  @Input() feeSplitIndex = 0;
  @Input() externalBroker = false;
  @Input() currencySymbol: string = '';
  @Input() showValidation = false;
  @Input() apacHiddenInputs = true;
  @Input() totalCbreAmount = 0;
  @Input() transactionType: string = '';
  @Input() disabled = false;
  @Input() transaction: DataObject = {};

  // TODO: These are not required, verify: update-internal-adjustment flow!!!
  @Input() amount;
  @Input() maxAmount;
  @Input() percent;
  // -------------------------
  @Input() maxPercent = 100;

  @Input() billingEntity = '';

  @Output() saveFeeSplit = new EventEmitter();
  @Output() deleteFeeSplit = new EventEmitter();

  uuid = uuid();
  feeSplitForm: UntypedFormGroup;

  roles$: Observable<FieldOptionsObject[]>;
  functionalUnits$: Observable<FieldOptionsObject[]>;
  functionalUnits: DataObject[] = [];
  // managingOffices;
  projectCodes$: Observable<any>;
  activities$: Observable<any>;
  sourceCodes$: Observable<any>;
  categories$: Observable<any>;
  userCountry: string;
  // value is true for gws & as only
  projectCodeRequired = false;

  selectedProjectCode: PsProjectObject;
  selectedActivity: FormServiceObject;
  selectedSourceCode: FormServiceObject;
  selectedFuMo = '';
  $projectCode = new BehaviorSubject('');
  $activity = new BehaviorSubject('');
  sourceCode$ = new BehaviorSubject({ sourceCode: '', billingEntity: '' });
  calculatedAmount = false;
  swalOptions: any;
  myFinance = '';
  isCategoryMandatory: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private messageService: MessageService,
    private userProfileService: UserProfileService,
    private feeSplitsService: FeeSplitsService) {
    this.swalOptions = this.getSwalOptions();
  }

  ngOnInit() {
    this.userCountry = this.userProfileService.getCountryLocalStorage();
    this.myFinance = this.userProfileService.getMyFinanceLocalStorage();
    this.projectCodeRequired = this.feeSplit.project_req;
    this.setupForm();
    this.setupFormSubscription();
    this.roles$ = this.formService.getFieldOptions('FeeSplit', 'role');
    this.functionalUnits$ = this.formService.getFieldOptions(this.transactionType, 'functional_unit_and_account')
    .pipe(
      tap((units: FieldOptionsObject[]) => {
        this.functionalUnits = units;
      })
    );

    this.activities$ = this.$activity
      .pipe(
        debounceTime(300),
        switchMap((value: string) => this.formService.searchActivitiesByProjectId(this.feeSplitForm.get('project')?.value || null, value))
      );

    this.categories$ = this.formService.getCategoriesBySourceCodeId(this.feeSplit.source_code);

    this.projectCodes$ = this.$projectCode
      .pipe(
        debounceTime(300),
        switchMap((value: string) => this.formService.getProjectCodes(value))
      );

    this.sourceCodes$ = this.sourceCode$
      .pipe(
        debounceTime(300),
        switchMap((value: { sourceCode: string, billingEntity: string }) => {
          return this.formService.getSourceCodes(value.sourceCode, value.billingEntity)
        }),
      );

    if (this.feeSplit.project && this.feeSplit.project_details) {
      this.selectedProjectCode = this.feeSplit.project_details;
      if (this.feeSplit.activity && this.feeSplit.activity_details) {
        this.selectedActivity = this.feeSplit.activity_details;
      }
    }

    if (this.feeSplit.source_code && this.feeSplit.source_code_details) {
      this.selectedSourceCode = this.feeSplit.source_code_details;
    }

    this.messageService.getMessage()
      .pipe(
        untilDestroyed(this),
        filter(message => message.model === 'feeSplits')
      )
      .subscribe(
        message => {
          if (message.model === 'feeSplits' && message.obj.id !== this.feeSplit.id && !this.feeSplit.bill_external) {
            this.feeSplit.primary_employee = message.obj.primary_employee ? null : false;
            this.togglingFormControl('primary_employee', message.obj.primary_employee, 'broadcast');
          }
        }
      );
  }

  projectCodeDisplay() {
    return this.selectedProjectCode ? this.selectedProjectCode.code + ': ' + this.selectedProjectCode.name : '';
  }

  ngOnChanges(changes: SimpleChanges) {
    const changesKeys = Object.keys(changes);
    if (changesKeys.length === 1 && changesKeys.includes('totalCbreAmount')) {
      const calculatedFlatAmount = this.calculateFeeSplitAmount(this.totalCbreAmount, this.feeSplit.percent_output);
      if (+this.feeSplit.flat_amount !== +calculatedFlatAmount) {
        // Calculate Amount
        this.feeSplit.flat_amount = calculatedFlatAmount;
        // Update View
        this.updateView({ 'flat_amount': this.feeSplit.flat_amount }, true);
      }
    }

    if (changesKeys.includes('billingEntity')) {
      this.sourceCode$.next({ sourceCode: '', billingEntity: this.billingEntity });
    }
  }

  ngOnDestroy() {
  }

  setDisabledState() {
    return this.disabled;
  }

  setupForm() {
    const [regRegion] = this.getBrokerRegionSetId(this.feeSplit);
    const params = {
      broker: [{ value: this.feeSplit.broker, disabled: this.setDisabledState() }, Validators.required],
      broker_details: [{ value: this.feeSplit.broker_details, disabled: this.setDisabledState() }],
      name: [{ value: this.feeSplit.name, disabled: this.setDisabledState() }, Validators.required],
      email: [{ value: this.feeSplit.email, disabled: this.setDisabledState() }, Validators.required],
      flat_amount: [{ value: this.feeSplit.flat_amount, disabled: this.setDisabledState() }],
      percent_output: [{ value: parseFloat(this.feeSplit.percent_output), disabled: this.disabled },
      { validators: [Validators.required, Validators.min(0), Validators.max(100)] }
      ],
      role: [{ value: this.feeSplit.role, disabled: this.setDisabledState() }],
      project: [{ value: this.feeSplit.project, disabled: this.setDisabledState() }],
      activity: [{ value: this.feeSplit.activity, disabled: this.setDisabledState() }],
      source_code: [{ value: this.feeSplit.source_code, disabled: this.setDisabledState() }],
      project_req: [this.feeSplit.project_req], // Not displayed in the UI
      // This is not longer used on Fee Splits. Is only for Fee Influencers
      external_broker: [{ value: this.feeSplit.external_broker, disabled: this.setDisabledState() }],
      managing_office: [{ value: this.feeSplit.managing_office, disabled: this.setDisabledState() }],
      company_office: [{ value: this.feeSplit.company_office, disabled: this.setDisabledState() }],
      functional_unit: [{ value: this.feeSplit.functional_unit, disabled: this.setDisabledState() }],
      account: [{ value: this.feeSplit.account, disabled: this.setDisabledState() }],
      client: [{ value: this.feeSplit.client, disabled: this.setDisabledState() }],
      pc_bu: [{ value: this.feeSplit.pc_bu, disabled: this.setDisabledState() }],
      legal_entity: [{ value: this.feeSplit.legal_entity, disabled: this.setDisabledState() }],
      // This field drives the logic behind Conjunctional Agent or External Broker
      bill_external: [{ value: this.feeSplit.bill_external, disabled: this.setDisabledState() }],
      disbursement_allocation_percent: [{ value: this.feeSplit.disbursement_allocation_percent || '0.00', disabled: true }],
      disbursement_allocation_amount: [{ value: this.feeSplit.disbursement_allocation_amount || '0.00', disabled: true }],
      smf: [{ value: this.feeSplit.smf || '0.00', disabled: true }],
      akol_ded: [{ value: this.feeSplit.akol_ded || '0.00', disabled: true }],
      actual_fee: [{ value: this.feeSplit.actual_fee || '0.00', disabled: true }],
      primary_employee: [{
        value: this.feeSplit.primary_employee,
        disabled: this.disabledPrimaryEmployee(this.feeSplit.bill_external, this.feeSplit.primary_employee)
      }],
      greenspace: [this.feeSplit.greenspace],
      license_number: [this.feeSplit.license_number],
      reg_region: [regRegion],
      la_setid: [this.feeSplit.la_setid],
      business_unit: [this.feeSplit.business_unit],
      business_unit_gl: [this.feeSplit.business_unit_gl],
      ext_affiliate_code: [{ value: this.feeSplit.ext_affiliate_code, disabled: true }]
    };
    const categoryOptional = {
      category: [{
        value: this.feeSplit.category,
        disabled: this.setDisabledState()
      }]
    };
    const categoryMandatory = {
      category: [{
        value: this.feeSplit.category,
        disabled: this.setDisabledState()
      }, Validators.required]
    };

    if (['AUS', 'NZL', 'JPN'].includes(this.userCountry) && this.myFinance === 'peoplesoft') {
      this.feeSplitForm = this.fb.group({ ...params, ...categoryOptional });
      this.isCategoryMandatory = false;
    } else {
      this.feeSplitForm = this.fb.group({ ...params, ...categoryMandatory });
      this.isCategoryMandatory = true;
    }
    this.formService.addLoadedForm(this.feeSplitForm, this.uuid);

    // INFO: This is to set the selectedFuMo value for the dropdown in the UI
    this.selectedFuMo = `${this.feeSplit.la_setid}-${this.feeSplit.functional_unit}-${this.feeSplit.managing_office}`;

    if (this.showValidation === true && this.feeSplitForm) {
      this.formService.showFormValidations(this.feeSplitForm);
    }
  }

  setupFormSubscription() {
    this.feeSplitForm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(1000),
        distinctUntilChanged((originalObj, newObj) => JSON.stringify(originalObj) === JSON.stringify(newObj)),
      )
      .subscribe(changes => {
        this.calculatedAmount = false;

        if (this.feeSplit.broker_details !== changes.broker_details) {
          changes.license_number = null;
          this.updateView({ 'license_number': changes.license_number });
        }

        if (this.feeSplit.bill_external !== changes.bill_external) {
          changes.disbursement_allocation_amount = '0.00';
          changes.disbursement_allocation_percent = '0.00';
          changes.primary_employee = false;
          this.resetManagingOffice();
          const managingOfficeChanges = this.getManagingOfficeChanges();
          changes = { ...changes, ...managingOfficeChanges };
        }

        if (+this.feeSplit.percent_output !== +changes.percent_output) {
          changes.flat_amount = this.calculateFeeSplitAmount(this.totalCbreAmount, changes.percent_output);
          this.updateView({ 'flat_amount': changes.flat_amount });
          this.calculatedAmount = true;
        }

        if (+this.feeSplit.flat_amount !== +changes.flat_amount && !this.calculatedAmount) {
          changes.percent_output = this.calculateFeeSplitPercent(this.totalCbreAmount, changes.flat_amount);
          this.updateView({ 'percent_output': changes.percent_output });
        }

        /* INFO: When the broker info is pre-populated using the user's session from backend
         and the user hasn't used the broker search at all */
        if (this.feeSplit.broker && this.feeSplit.broker_details &&
          this.feeSplit.broker_details.length > 0 && !this.feeSplit.functional_unit && !this.feeSplit.managing_office) {
          const brokerDetailsChanges = this.getBrokerDetails(this.feeSplit.broker_details);
          changes.managing_office = brokerDetailsChanges.managing_office.id;
          changes.functional_unit = brokerDetailsChanges.functional_unit.id;
          changes.company_office = `${brokerDetailsChanges.la_setid} - ${brokerDetailsChanges.functional_unit.name} - ${brokerDetailsChanges.managing_office.name}`;
          changes.legal_entity = brokerDetailsChanges.business_unit_gl;
          changes.client = brokerDetailsChanges.client;
          changes.reg_region = brokerDetailsChanges.reg_region;
          changes.la_setid = brokerDetailsChanges.la_setid;
          changes.business_unit = brokerDetailsChanges.business_unit;
          changes.business_unit_gl = brokerDetailsChanges.business_unit_gl;
        }
        if (['valuation', 'consultation'].includes(this.transactionType) && this.userCountry === 'IND') {
          if (this.feeSplit.functional_unit !== changes.functional_unit) {
            const selectedFunctionalUnit: DataObject = this.functionalUnits.find(item => item.value === changes.functional_unit) || {};
            if (Object.keys(selectedFunctionalUnit).length !== 0) {
              changes.account = selectedFunctionalUnit.account;
            } else {
              changes.account = null; // Reset account if functional unit is not found
            }
          }
          // If primary_employee is not selected, set functional_unit based on broker_details
          if (!changes.primary_employee) {
            changes.functional_unit = (this.feeSplit?.broker_details && this.feeSplit.broker_details.length > 0 
              && this.feeSplit.broker_details[0]?.functional_unit?.id) || changes.functional_unit;
          }
        }
        this.saveFeeSplit.emit({ ...changes, id: this.feeSplit.id, debug: 'Value Changes...' });
      });
  }

  changeFuMo(event: any) {
    if (event.value) {
      const [la_setid, functional_unit, managing_office] = (event.value).split('-');
      if (functional_unit !== this.feeSplit.functional_unit || managing_office !== this.feeSplit.managing_office || la_setid !== this.feeSplit.la_setid) {
        const brokerDetails = this.getBrokerDetailsByFuMo(this.feeSplit.broker_details, functional_unit, managing_office, la_setid);
        const FuMoChanges = {
          managing_office: brokerDetails.managing_office.id,
          functional_unit: brokerDetails.functional_unit.id,
          company_office: `${brokerDetails.la_setid} - ${brokerDetails.functional_unit.name} - ${brokerDetails.managing_office.name}`,
          legal_entity: brokerDetails.business_unit_gl,
          client: brokerDetails.client,
          reg_region: brokerDetails.reg_region,
          la_setid: brokerDetails.la_setid,
          business_unit: brokerDetails.business_unit,
          business_unit_gl: brokerDetails.business_unit_gl,
          // Extra fields for FIN-8142
          project: null,
          activity: null,
          source_code: null,
          category: null
        };
        this.updateView(FuMoChanges, true);
      }
    }
  }

  brokerSelected(broker) {
    if (!this.showGreenSpace(broker.broker_details)) {
      this.updateView({ greenspace: false }, false);
    }

    if (this.feeSplitForm.get('bill_external')?.value || broker.is_external) {
      this.updateView({
        broker: null,
        broker_details: {},
        name: broker.name,
        email: broker.email,
        managing_office: null,
        functional_unit: null,
        client: null,
        legal_entity: null,
        reg_region: null,
        la_setid: null,
        business_unit: null,
        business_unit_gl: null
      }, true);

      if (broker.broker_details && broker.broker_details.emplid) {
        this.patchBrokerSelected(broker, true);
      }

    } else if (broker.broker_details && broker.broker_details.emplid) {
      this.patchBrokerSelected(broker, true);
    }
  }

  patchBrokerSelected(broker: any, emitEvent: boolean) {
    const selectedBroker = {
      broker: broker.broker_details.emplid,
      broker_details: broker.broker_details,
      name: broker.broker_details.preferred_name,
      email: broker.broker_details.emailid,
      company_office: `${broker.broker_details.la_setid} - ${broker.broker_details.functional_unit.name} - ${broker.broker_details.managing_office.name}`,
      reg_region: broker.broker_details.reg_region,
      managing_office: broker.broker_details.managing_office.id,
      functional_unit: broker.broker_details.functional_unit.id,
      client: broker.broker_details.client,
      legal_entity: broker.broker_details.business_unit_gl,
      la_setid: broker.broker_details.la_setid,
      business_unit: broker.broker_details.business_unit,
      business_unit_gl: broker.broker_details.business_unit_gl,
      primary_employee: false,
      // FIN-8142
      project: null,
      activity: null,
      source_code: null,
      category: null,
      project_req: false
    };
    this.updateView(selectedBroker, emitEvent);
    this.feeSplit = { ...this.feeSplit, ...selectedBroker };
  }


  projectCodeSelected(project: any, event: any) {
    if (!event.source.selected) {
      return;
    }
    this.selectedProjectCode = project;
    const patch = {
      project: project.id,
      activity: null // Nullifying dependency
    };
    this.feeSplitForm.patchValue(patch);
    this.$projectCode.next('');
  }

  projectCodeTyped(event) {
    this.$projectCode.next(event.target.value);
    if (event.target.value === '') {
      this.selectedActivity = { id: '', name: '', code: '' };
    }
  }

  activitySelected(activity: any, event: any): void {
    if (!event.source.selected) {
      return;
    }
    this.selectedActivity = activity;
    this.feeSplitForm.patchValue({
      activity: activity.id
    });
    this.$activity.next('');
  }

  activityTyped(event) {
    this.$activity.next(event.target.value);
  }

  sourceCodeSelected(sourceCode: any, event: any): void {
    if (!event.source.selected) {
      return;
    }
    this.selectedSourceCode = sourceCode;
    this.feeSplitForm.patchValue({
      source_code: sourceCode.id,
      category: null // Nullifying dependency
    });
  }

  sourceCodeTyped(event: any): void {
    const sourceCode = event.target.value;
    this.sourceCode$.next({ sourceCode, billingEntity: this.billingEntity });
    if (sourceCode === '') {
      this.categories$ = of([]);

      this.feeSplitForm.patchValue({
        source_code: null,
        category: null
      });
    }
  }

  showDeleteAlert() {
    this.warnAlert.fire().then(response => {
      if (response.value) {
        this.deleteFeeSplit.emit(this.feeSplit.id);
      }
    });
  }

  disabledPrimaryEmployee(billExternal: any, primaryEmployee: any) {
    if (billExternal === true || primaryEmployee === null || this.disabled) {
      return true;
    }
    return false;
  }

  calculateFeeSplitAmount(totalCbreAmount: number, percent: number) {
    return this.feeSplitsService.calculateFeeSplitAmount(totalCbreAmount, percent);
  }

  calculateFeeSplitPercent(totalCbreAmount: number, amount: number) {
    return this.feeSplitsService.calculateFeeSplitPercent(totalCbreAmount, amount);
  }

  updateView(fields: any, emitEvent = false) {
    // by default, patchValue triggers value changes
    if (this.feeSplitForm) {
      this.feeSplitForm.patchValue(fields, { emitEvent: emitEvent });
    }
  }

  togglingFormControl(formControl: string, state: boolean = true, origin: string = '') {
    const localState = state ? 'disable' : 'enable';
    if (formControl === 'primary_employee') {
      if ((origin === 'bill_external' && state === false) ||
        (origin === 'broadcast' && state === true)) {
        this.feeSplit.primary_employee = null;
        this.updateView({ 'primary_employee': false });
      }
      if (origin === 'bill_external' && state === true) {
        this.feeSplit.primary_employee = null;
        this.updateView({ 'primary_employee': null });
        this.feeSplit.license_number = null;
        this.updateView({ 'license_number': null });
        // Broadcast primary employee
        this.messageService.sendMessage({
          model: 'feeSplits',
          type: 'PUT',
          title: 'Updating primary employee',
          text: '',
          obj: { 'id': this.feeSplit.id, 'primary_employee': false, 'origin': 'bill_external' },
        });
      }
    }
    if (state === true && origin === 'bill_external') {
      this.feeSplitForm.patchValue({
        greenspace: false,
      });
    }
    this.feeSplitForm.controls[formControl][localState]({ emitEvent: false });
  }

  broadcastPrimaryEmployee() {
    const primaryEmployee = this.feeSplitForm.get('primary_employee')?.value;
    // Broadcast primary employee
    this.messageService.sendMessage({
      model: 'feeSplits',
      type: 'PUT',
      title: 'Updating primary employee',
      text: '',
      obj: { 'id': this.feeSplit.id, 'primary_employee': primaryEmployee, 'origin': 'primary_employee' }
    });
  }

  showGreenSpace(feeSplit: any): boolean {
    if (feeSplit.functional_unit) {
      return (feeSplit.functional_unit === '100112' && this.userCountry === 'AUS');
    }
    return false;
  }

  showLicenseNumberCheck() {
    return this.userProfileService.showLicenseNumber(this.transactionType, this.userCountry);
  }

  showSMF() {
    return this.userProfileService.showSMF(this.userCountry);
  }

  getSwalOptions() {
    const options = {
      showConfirmButton: true,
      showCloseButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      showCancelButton: true
    };
    return this.formService.getSwalOptions(options);
  }

  selectedActivityName() {
    return this.selectedActivity ? this.selectedActivity.name : '';
  }

  selectedSourceCodeName() {
    return this.selectedSourceCode ? this.selectedSourceCode.name : '';
  }

  getBrokerDetails(brokerDetails: any) {
    return this.feeSplitsService.getBrokerDetails(brokerDetails);
  }

  getBrokerRegionSetId(feeSplit: any) {
    return this.feeSplitsService.getBrokerRegionSetId(feeSplit);
  }

  getBrokerDetailsByFuMo(brokerDetails: any, functionalUnit: number, managingOffice: number, laSetId: string) {
    return this.feeSplitsService.getBrokerDetailsByFuMo(brokerDetails, functionalUnit, managingOffice, laSetId);
  }

  // Reset the fields related to the Broker
  resetManagingOffice() {
    const obj = {
      broker: null,
      broker_details: {},
      name: null,
      email: null,
      managing_office: null,
      functional_unit: null,
      client: null,
      legal_entity: null,
      reg_region: null,
      la_setid: null,
      business_unit: null,
      business_unit_gl: null
    };
    if (this.feeSplitForm.get('bill_external')?.value === false) {
      this.updateView({ ...obj, ...{ company_office: null } }, false);
    } else {
      this.updateView(obj, false);
    }
  }

  getManagingOfficeChanges() {
    const obj = {
      broker: null,
      broker_details: {},
      name: null,
      email: null,
      managing_office: null,
      functional_unit: null,
      client: null,
      legal_entity: null,
      reg_region: null,
      la_setid: null,
      business_unit: null,
      business_unit_gl: null,
      // FIN-8142
      project: null,
      activity: null,
      source_code: null,
      category: null,
      project_req: false
    };
    if (this.feeSplitForm.get('bill_external')?.value === false) {
      return ({ ...obj, ...{ company_office: null } });
    } else {
      return obj;
    }
  }

  get singleCompanyOffice() {
    return this.feeSplitsService.getCompanyOffice(this.feeSplit);
  }
}
